import { useCallback } from 'react';

import type { BaseNavigateOptions } from '../navigate';
import { unsafeNavigate } from '../navigate';
import { useCreateUrlFromRootMap } from './useCreateUrlFromRootMap';
import type { FeatureRouteConfig, NavigateOptionsWithOptionalBase } from './FeatureNavigateTypes';

export type UseFeatureNavigateReturnType<RouteConfig extends FeatureRouteConfig> = <
    Path extends RouteConfig['paths'],
>(
    path: Path,
    ...[options]: NavigateOptionsWithOptionalBase<RouteConfig['baseUrl'], Path, BaseNavigateOptions>
) => void;

export function useFeatureNavigate<FeatureId extends never>(
    /**
     * FeatureId used for dynamic lookup to build absolute URL, since user provides only relative path inside the feature
     */
    featureId: FeatureId,
): UseFeatureNavigateReturnType<{ paths: string; baseUrl: string }> {
    const createUrlFromRootMap = useCreateUrlFromRootMap(featureId);

    return useCallback(
        (path, ...[options]) => {
            const { queryParameters = {}, replace, ...withoutQueryParameters } = options ?? {};
            unsafeNavigate(
                createUrlFromRootMap(path, {
                    pathParameters: withoutQueryParameters,
                    queryParameters,
                }),
                {
                    replace,
                },
            );
        },
        [createUrlFromRootMap],
    );
}
